/* eslint-disable @typescript-eslint/no-explicit-any */

// ***** Import from packages *****
import axios, {
  AxiosRequestConfig,
  AxiosResponse,
  AxiosError,
  AxiosHeaderValue
} from "axios";

// ***** Import from files *****
import { BASE_API_URL } from "@/src/constants/api/config";
import { USER_DATA } from "@/constants/constants";

// ***** Global AbortController for canceling all requests *****
let abortController = new AbortController();

/**
 * Cancels all currently in-progress API requests
 */
export const cancelAllRequests = () => {
  abortController.abort();
  // Reset after abort so that future requests (e.g. on new login) can proceed
  abortController = new AbortController();
};

// ***** Type definitions *****
type ApiMethod = "get" | "post" | "put" | "patch" | "delete";

interface ApiResponse<T = unknown> {
  status: number;
  data?: T;
  message?: string;
  headers?: AxiosHeaderValue | null
  ok?: boolean;
}

interface ApiError {
  status: number;
  message: string;
  data?: unknown;
}

interface AxiosErrorCustom extends AxiosError {
  response?: AxiosResponse<unknown> & {
    data?: {
      message?: string;
    };
  };
}

interface ApiOptions extends AxiosRequestConfig {
  headers?: Record<string, string>;
  responseType?: "json" | "stream";
}

// ***** Constants *****
const API_CONFIG = {
  baseURL: BASE_API_URL,
  timeout: 30000, // 30 seconds
  headers: {
    "Content-Type": "application/json"
  }
} as const;

// ***** Utility functions *****
const getUnStringified = (token: string | null): string => {
  return token?.replaceAll('"', "") ?? "";
};

const getToken = (): string | null => {
  const userData = sessionStorage.getItem(USER_DATA);
  if (userData) {
    const userDataObj = JSON.parse(userData);
    return userDataObj.token;
  }
  return null;
};

const createHeaders = (
  isNonAuthenticated: boolean,
  customHeaders: Record<string, string> = {}
): Record<string, string> => {
  const headers = { ...API_CONFIG.headers, ...customHeaders, Authorization: "" };

  if (!isNonAuthenticated) {
    const token = getUnStringified(getToken());
    if (token) {
      headers.Authorization = `Token ${token}`;
    }
  }

  return headers;
};

/**
 * Makes an HTTP request to the specified endpoint
 * @param endpoint - API endpoint
 * @param data - Request payload
 * @param type - HTTP method
 * @param isNonAuthenticated - Whether to skip authentication
 * @param customHeader - Additional headers
 * @param isManualEndpoint - Whether to use endpoint as complete URL
 * @param customOptions - Additional axios options
 * @param isStream - Whether response is a stream
 */
export const api = async <T = unknown>(
  endpoint: string,
  data: unknown = {},
  type: ApiMethod,
  isNonAuthenticated = false,
  customHeader: Record<string, string> = {},
  isManualEndpoint = false,
  customOptions: ApiOptions = {},
  isStream = false
): Promise<ApiResponse<T | Blob>> => {
  try {
    const headers = createHeaders(isNonAuthenticated, customHeader);
    const url = isManualEndpoint ? endpoint : `${API_CONFIG.baseURL || ''}${endpoint}`;
    const config: AxiosRequestConfig = {
      ...customOptions,
      headers,
      responseType: isStream ? "blob" : "json",
      // Use signal from customOptions if provided, otherwise use global abortController
      signal: customOptions.signal || abortController.signal
    };

    let response: AxiosResponse<T>;

    switch (type) {
      case "get":
        response = await axios.get<T>(url, config);
        break;
      case "delete":
        response = await axios.delete<T>(url, { ...config, data });
        break;
      case "post":
      case "put":
      case "patch":
        response = await axios[type]<T>(url, data, config);
        break;
      default:
        throw new Error(`Invalid request type: ${type}`);
    }

    return {
      status: response.status,
      data: isStream ? (response.data as Blob) : response.data,
      headers: isStream ? response.headers as AxiosHeaderValue : null,
      ok: true
    };

  } catch (error) {
    const axiosError = error as AxiosErrorCustom;
    const apiError: ApiError = {
      status: axiosError.response?.status ?? 500,
      message: axiosError.response?.data?.message ?? axiosError.message ?? 'Unknown error occurred',
      data: axiosError.response?.data
    };
    
    return {
      status: apiError.status,
      message: apiError.message,
      data: apiError.data as T,
      ok: false
    };
  }
};

// Optional: Add request/response interceptors
axios.interceptors.request.use(
  (config) => {
    // Add any request transformations
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

axios.interceptors.response.use(
  (response) => {
    // Add any response transformations
    return response;
  },
  (error) => {
    // Handle specific error cases
    if (error.response?.status === 401) {
      // Handle unauthorized access - clear ONLY this tab's session data
      sessionStorage.removeItem(USER_DATA);
      // Note: Do NOT clear localStorage - other tabs might be using it
      
      // Check if we're already on the login page to prevent infinite redirect
      const currentPath = window.location.pathname;
      if (currentPath !== '/login') {
        // Redirect to login page
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);